import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatChannel,
  ChatChannelTypeEnum,
  ChatConversation,
  ChatMessage,
  ChatMessageTypeEnum,
  ChatSearchData,
  ChatSearchParams,
  ChatSearchPerson,
  ChatService,
  ChatUserDisplayMap,
} from 'lib-trend-core';
import { Subject, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';
import { buildExcerpt, ChatHighlightSegment, highlightSegments } from '../../chat-switcher/chat-fuzzy.util';
import { ChatShellService } from '../../services/chat-shell.service';
import { conversationsSelector, userDisplaySelector } from '../../state/chat.selectors';
import { openChatConversation, openChatDirect } from '../../chat-switcher/chat-open-target.util';
import { resolveChatInitials } from '../../chat-initials.util';
import { stripChatMarkdown } from '../../pipes/chat-preview.util';

type ChatSearchTab = 'messages' | 'conversations' | 'people';

/** One active filter, rendered as a removable chip. */
interface ChatSearchChip {
  key: 'from' | 'in' | 'type' | 'dateFrom' | 'dateTo';
  label: string;
}

interface ChatSearchMessageRow {
  id: string;
  channelId: string;
  conversationName: string;
  conversationIcon: string;
  authorName: string;
  authorImage?: string;
  authorInitial: string;
  dateLabel: string;
  typeIcon: string;
  segments: ChatHighlightSegment[];
  openable: boolean;
}

interface ChatSearchConversationRow {
  id: string;
  name: string;
  icon: string;
  isMember: boolean;
  segments: ChatHighlightSegment[];
}

interface ChatSearchPersonRow {
  id: string;
  name: string;
  email: string;
  image?: string;
  initial: string;
  segments: ChatHighlightSegment[];
}

/** Option of the from:/in: selects (built from data the client already has). */
interface ChatSearchOption {
  id: string;
  label: string;
}

const SEARCH_LIMIT = 30;

/**
 * Chat search view (SPEC-2026-07-internal-chat, T-22 / RF-27 / AC-23).
 *
 * A pane of the fullscreen chat workspace (reachable from the quick
 * switcher's "search messages" row, which hands over the typed term via
 * `initialTerm` — this used to be the routed `/chat/search?q=`). Calls the
 * read-only `GET /api/chat/search` with combinable filters —
 * `from:` sender · `in:` conversation · `type:` message type · date range —
 * and renders the three result groups the endpoint returns (messages /
 * conversations / people) with the matched term highlighted.
 *
 * PRIVACY: the endpoint scopes every result to (my member channels ∪ public
 * channels of my company) server-side; this view renders exactly what came
 * back and never hydrates anything else. Conversations the caller is not a
 * member of are shown as non-openable (joining lives in the sidebar's channel
 * directory).
 */
@Component({
  selector: 'chat-search',
  templateUrl: './chat-search.component.html',
  styleUrls: ['./chat-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatSearchComponent extends AbstractComponent implements OnInit, OnChanges {

  /** Term handed over by the switcher/workspace (used to be `?q=`). */
  @Input() initialTerm = '';

  /** "Back to the conversation" — the workspace swaps the pane back. */
  @Output() closed = new EventEmitter<void>();

  readonly messageTypes = ChatMessageTypeEnum;

  term = '';
  filterFrom = '';
  filterIn = '';
  filterType = '';
  filterDateFrom: Date | null = null;
  filterDateTo: Date | null = null;

  activeTab: ChatSearchTab = 'messages';
  hasError = false;
  searched = false;

  messageRows: ChatSearchMessageRow[] = [];
  conversationRows: ChatSearchConversationRow[] = [];
  peopleRows: ChatSearchPersonRow[] = [];

  peopleOptions: ChatSearchOption[] = [];
  conversationOptions: ChatSearchOption[] = [];
  chips: ChatSearchChip[] = [];

  private conversations: ChatConversation[] = [];
  private userDisplay: ChatUserDisplayMap = {};
  private readonly queries = new Subject<void>();

  constructor(
    injector: Injector,
    private readonly store: Store,
    private readonly chatService: ChatService,
    private readonly chatShell: ChatShellService,
  ) {
    super(injector);
  }

  /** The switcher hands over the already-typed term (used to be `/chat/search?q=`). */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['initialTerm']) return;
    const q = this.initialTerm ?? '';
    if (q && q !== this.term) {
      this.term = q;
      this.search();
    }
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(conversationsSelector),
      this.store.select(userDisplaySelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([conversations, userDisplay]) => {
        this.conversations = conversations;
        this.userDisplay = userDisplay;
        this.buildOptions();
      });

    this.queries
      .pipe(
        debounceTime(350),
        switchMap(() => {
          const params = this.buildParams();
          if (!params) {
            this.loading = false;
            this.searched = false;
            return of<ChatSearchData | null>(null);
          }
          return this.chatService.search(params).pipe(
            map((envelope) => envelope?.data ?? null),
            catchError(() => {
              this.hasError = true;
              return of<ChatSearchData | null>(null);
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => {
        this.loading = false;
        this.applyResults(data);
      });

  }

  /** (Re)runs the search — also the retry path of the error state. */
  search(): void {
    this.hasError = false;
    this.loading = !!this.buildParams();
    this.buildChips();
    this.queries.next();
  }

  selectTab(tab: ChatSearchTab): void {
    this.activeTab = tab;
  }

  clearFilters(): void {
    this.filterFrom = '';
    this.filterIn = '';
    this.filterType = '';
    this.filterDateFrom = null;
    this.filterDateTo = null;
    this.search();
  }

  removeChip(chip: ChatSearchChip): void {
    switch (chip.key) {
      case 'from': this.filterFrom = ''; break;
      case 'in': this.filterIn = ''; break;
      case 'type': this.filterType = ''; break;
      case 'dateFrom': this.filterDateFrom = null; break;
      case 'dateTo': this.filterDateTo = null; break;
      default: break;
    }
    this.search();
  }

  get hasFilters(): boolean {
    return this.chips.length > 0;
  }

  get hasAnyResult(): boolean {
    return this.messageRows.length > 0 || this.conversationRows.length > 0 || this.peopleRows.length > 0;
  }

  backToWorkspace(): void {
    this.closed.emit();
  }

  /** Jumps to the conversation at the matched message. */
  openMessage(row: ChatSearchMessageRow): void {
    if (!row.openable) return;
    openChatConversation(this.store, this.chatShell, row.channelId, row.id);
  }

  openConversation(row: ChatSearchConversationRow): void {
    if (!row.isMember) return;
    openChatConversation(this.store, this.chatShell, row.id);
  }

  openPerson(row: ChatSearchPersonRow): void {
    openChatDirect(this.store, this.chatShell, this.getIDCurrentUser(), row.id, this.conversations);
  }

  trackMessage(_index: number, row: ChatSearchMessageRow): string {
    return row.id;
  }

  trackConversation(_index: number, row: ChatSearchConversationRow): string {
    return row.id;
  }

  trackPerson(_index: number, row: ChatSearchPersonRow): string {
    return row.id;
  }

  trackSegment(index: number, segment: ChatHighlightSegment): string {
    return `${index}:${segment.text}`;
  }

  trackOption(_index: number, option: ChatSearchOption): string {
    return option.id;
  }

  /** `null` ⇒ nothing to ask for (no term and no filter) — mirrors the backend. */
  private buildParams(): ChatSearchParams | null {
    const q = this.term.trim();
    const hasFilters = !!(this.filterFrom || this.filterIn || this.filterType || this.filterDateFrom || this.filterDateTo);
    if (!q && !hasFilters) return null;

    const params: ChatSearchParams = { limit: SEARCH_LIMIT };
    if (q) params.q = q;
    if (this.filterFrom) params.from = this.filterFrom;
    if (this.filterIn) params.in = this.filterIn;
    if (this.filterType) params.type = this.filterType;
    const dateFrom = this.startOfDay(this.filterDateFrom);
    const dateTo = this.endOfDay(this.filterDateTo);
    if (dateFrom) params.dateFrom = dateFrom.toISOString();
    if (dateTo) params.dateTo = dateTo.toISOString();

    return params;
  }

  private applyResults(data: ChatSearchData | null): void {
    if (!data) {
      this.messageRows = [];
      this.conversationRows = [];
      this.peopleRows = [];
      return;
    }

    this.searched = true;
    const display: ChatUserDisplayMap = { ...this.userDisplay, ...(data.userDisplay ?? {}) };

    this.messageRows = (data.messages ?? []).map((message) => this.buildMessageRow(message, display));
    this.conversationRows = (data.channels ?? []).map((channel) => this.buildConversationRow(channel));
    this.peopleRows = (data.people ?? []).map((person) => this.buildPersonRow(person));
  }

  private buildMessageRow(message: ChatMessage, display: ChatUserDisplayMap): ChatSearchMessageRow {
    const conversation = this.conversations.find((item) => item._id === String(message.channel));
    const authorName = display[String(message.sender)]?.name ?? this.t('chat.search.unknownAuthor');
    // Stripped BEFORE the excerpt window is cut: the markers are not searchable
    // text, so leaving them in would spend the window on `**`/`](https://…` and
    // could even push the highlighted match out of the visible slice.
    const preview = message.type === ChatMessageTypeEnum.TEXT
      ? stripChatMarkdown(message.content)
      : (message.fileName || stripChatMarkdown(message.content));

    return {
      id: message._id,
      channelId: String(message.channel),
      conversationName: conversation
        ? this.resolveName(conversation)
        : this.t('chat.bubble.channelConversation'),
      conversationIcon: this.resolveIcon(conversation?.type),
      authorName,
      authorImage: display[String(message.sender)]?.image,
      authorInitial: resolveChatInitials(authorName || '?'),
      dateLabel: this.formatDate(message.createdAt),
      typeIcon: this.resolveTypeIcon(message.type),
      segments: highlightSegments(buildExcerpt(preview, this.term), this.term),
      // Only conversations I am a member of can be opened — the search scope
      // also includes public channels I have not joined.
      openable: !!conversation,
    };
  }

  private buildConversationRow(channel: ChatChannel): ChatSearchConversationRow {
    const conversation = this.conversations.find((item) => item._id === channel._id);
    const name = conversation ? this.resolveName(conversation) : (channel.name ?? this.t('chat.bubble.channelConversation'));

    return {
      id: channel._id,
      name,
      icon: this.resolveIcon(channel.type),
      isMember: !!conversation,
      segments: highlightSegments(name, this.term),
    };
  }

  private buildPersonRow(person: ChatSearchPerson): ChatSearchPersonRow {
    const name = person.name ?? '';

    return {
      id: person._id,
      name,
      email: person.email ?? '',
      image: person.image,
      initial: resolveChatInitials(name || '?'),
      segments: highlightSegments(name, this.term),
    };
  }

  private buildOptions(): void {
    const currentUserId = this.getIDCurrentUser();

    this.peopleOptions = Object.keys(this.userDisplay)
      .map((id) => ({ id, label: this.userDisplay[id]?.name ?? '' }))
      .filter((option) => !!option.label)
      .sort((a, b) => a.label.localeCompare(b.label));

    this.conversationOptions = this.conversations
      .map((conversation) => ({ id: conversation._id, label: this.resolveName(conversation, currentUserId) }))
      .filter((option) => !!option.label)
      .sort((a, b) => a.label.localeCompare(b.label));

    this.buildChips();
  }

  private buildChips(): void {
    const chips: ChatSearchChip[] = [];

    if (this.filterFrom) {
      const person = this.peopleOptions.find((option) => option.id === this.filterFrom);
      chips.push({ key: 'from', label: this.t('chat.search.chipFrom', { value: person?.label ?? this.filterFrom }) });
    }
    if (this.filterIn) {
      const conversation = this.conversationOptions.find((option) => option.id === this.filterIn);
      chips.push({ key: 'in', label: this.t('chat.search.chipIn', { value: conversation?.label ?? this.filterIn }) });
    }
    if (this.filterType) {
      chips.push({ key: 'type', label: this.t('chat.search.chipType', { value: this.t(this.typeLabelKey(this.filterType)) }) });
    }
    const dateFrom = this.startOfDay(this.filterDateFrom);
    if (dateFrom) {
      chips.push({ key: 'dateFrom', label: this.t('chat.search.chipDateFrom', { value: this.formatDay(dateFrom) }) });
    }
    const dateTo = this.endOfDay(this.filterDateTo);
    if (dateTo) {
      chips.push({ key: 'dateTo', label: this.t('chat.search.chipDateTo', { value: this.formatDay(dateTo) }) });
    }

    this.chips = chips;
  }

  typeLabelKey(type: string): string {
    switch (type) {
      case ChatMessageTypeEnum.IMAGE: return 'chat.search.typeImage';
      case ChatMessageTypeEnum.AUDIO: return 'chat.search.typeAudio';
      case ChatMessageTypeEnum.VIDEO: return 'chat.search.typeVideo';
      case ChatMessageTypeEnum.DOCUMENT: return 'chat.search.typeDocument';
      default: return 'chat.search.typeText';
    }
  }

  private resolveTypeIcon(type: ChatMessageTypeEnum): string {
    switch (type) {
      case ChatMessageTypeEnum.IMAGE: return 'ri-image-line';
      case ChatMessageTypeEnum.AUDIO: return 'ri-mic-line';
      case ChatMessageTypeEnum.VIDEO: return 'ri-video-line';
      case ChatMessageTypeEnum.DOCUMENT: return 'ri-file-line';
      default: return 'ri-message-2-line';
    }
  }

  /** Same naming rules as the sidebar (DIRECT/GROUP conversations are unnamed). */
  private resolveName(conversation: ChatConversation, currentUserId = this.getIDCurrentUser()): string {
    switch (conversation.type) {
      case ChatChannelTypeEnum.DIRECT: {
        const otherId = (conversation.members ?? []).find((id) => id !== currentUserId) ?? currentUserId;
        return this.userDisplay[otherId]?.name ?? this.t('chat.bubble.directConversation');
      }
      case ChatChannelTypeEnum.GROUP: {
        if (conversation.name) return conversation.name;
        const names = (conversation.members ?? [])
          .filter((id) => id !== currentUserId)
          .map((id) => this.userDisplay[id]?.name)
          .filter((name): name is string => !!name);
        return names.length > 0 ? names.slice(0, 3).join(', ') : this.t('chat.bubble.groupConversation');
      }
      default:
        return conversation.name ?? this.t('chat.bubble.channelConversation');
    }
  }

  private resolveIcon(type: ChatChannelTypeEnum | undefined): string {
    switch (type) {
      case ChatChannelTypeEnum.DIRECT: return 'ri-user-3-line';
      case ChatChannelTypeEnum.GROUP: return 'ri-group-line';
      case ChatChannelTypeEnum.PRIVATE: return 'ri-lock-line';
      default: return 'ri-hashtag';
    }
  }

  /** Numeric-only formats — locale-neutral (dd/MM/yyyy HH:mm). */
  private formatDate(value: Date | string | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${this.formatDay(date)} ${this.pad(date.getHours())}:${this.pad(date.getMinutes())}`;
  }

  private formatDay(date: Date): string {
    return `${this.pad(date.getDate())}/${this.pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  }

  private pad(value: number): string {
    return String(value).padStart(2, '0');
  }

  /** `null` for a missing or unparseable value — never sends "Invalid Date". */
  private startOfDay(date: Date | null): Date | null {
    const copy = date ? new Date(date) : null;
    if (!copy || Number.isNaN(copy.getTime())) return null;
    copy.setHours(0, 0, 0, 0);
    return copy;
  }

  private endOfDay(date: Date | null): Date | null {
    const copy = date ? new Date(date) : null;
    if (!copy || Number.isNaN(copy.getTime())) return null;
    copy.setHours(23, 59, 59, 999);
    return copy;
  }
}
